import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { CALL_OUTCOMES, LOGGABLE_ACTIVITY_TYPES, type ActivityType } from '@/types/app';
import { useLogActivity } from './useLeads';

interface Props {
  open: boolean;
  leadId: string;
  initialType?: ActivityType;
  onClose: () => void;
}

export function LogActivityDialog({ open, leadId, initialType = 'call', onClose }: Props) {
  const { t } = useTranslation();
  const log = useLogActivity();

  const [type, setType] = useState<ActivityType>(initialType);
  const [outcome, setOutcome] = useState('answered');
  const [minutes, setMinutes] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isCall = type === 'call';

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      await log.mutateAsync({
        leadId,
        type,
        body: body.trim() || null,
        // The database rejects an outcome on anything but a call, and rejects a call
        // without one (#14) — mirrored here so the form never sends a losing request.
        outcome: isCall ? (outcome as never) : null,
        durationSeconds: isCall && minutes ? Math.round(Number(minutes) * 60) : null,
      });
      setBody('');
      setMinutes('');
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal open={open} title={t('leads.logActivity')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Select
          label={t('leads.activityType')}
          value={type}
          onChange={(e) => setType(e.target.value as ActivityType)}
          options={LOGGABLE_ACTIVITY_TYPES.map((a) => ({
            value: a,
            label: t(`activities.${a}` as 'activities.call'),
          }))}
        />

        {isCall ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t('leads.callOutcome')}
              required
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              options={CALL_OUTCOMES.map((o) => ({
                value: o,
                label: t(`callOutcomes.${o}` as 'callOutcomes.answered'),
              }))}
            />
            <Input
              label={t('leads.durationMinutes')}
              type="number"
              min={0}
              step="1"
              dir="ltr"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="activity-body" className="text-sm font-medium text-slate-700">
            {t('leads.activityNote')}
          </label>
          <textarea
            id="activity-body"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900"
          />
        </div>

        <p className="text-xs text-slate-500">{t('leads.appendOnlyNote')}</p>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={log.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
